import React, { useState, useEffect, useCallback, useRef } from "react";
import { Video, Upload, Trash2, Eye, EyeOff, Scissors, Check, X, AlertTriangle, Loader2, Play, Pause, Film } from "lucide-react";
import { supabase } from "../lib/supabase";

const T = {
  green: "#1e9254", greenDark: "#155c36", greenTint: "#eef8f3", greenBd: "#a7d9bc",
  border: "#e4e7ef", borderFaint: "#eef0f4", card2: "#f7f8fa",
  text: "#0f1117", textMid: "#2d3549", sub: "#4a5268", faint: "#8b93a7", ghost: "#c7cddb",
  amber: "#92400e", amberTint: "#fefaf1", amberBd: "#f3d99a",
  red: "#b3261e", redTint: "#fdf3f2", redBd: "#f0c9c5",
  serif: "'Instrument Serif', Georgia, serif",
  font: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
  r10: "10px", r12: "12px", r14: "14px", r16: "16px", rFull: "999px",
  s1: "0 1px 3px rgba(13,46,26,0.05)", s2: "0 4px 16px rgba(13,46,26,0.07)", s3: "0 10px 34px rgba(13,46,26,0.14)",
  sGreen: "0 6px 20px rgba(30,146,84,0.22)",
  tx: "all 0.18s cubic-bezier(.4,0,.2,1)",
};

if (typeof document !== "undefined" && !document.getElementById("__vm_fonts")) {
  const l = document.createElement("link");
  l.id = "__vm_fonts";
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap";
  document.head.appendChild(l);
}
if (typeof document !== "undefined" && !document.getElementById("__vm_styles")) {
  const s = document.createElement("style");
  s.id = "__vm_styles";
  s.textContent = `
    @keyframes vmSpin { to { transform: rotate(360deg); } }
    @keyframes vmPulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
    .vm-row { transition:${T.tx}; }
    .vm-row:hover { border-color:${T.greenBd} !important; box-shadow:${T.s1}; }
    .vm-icon-btn { transition:${T.tx}; }
    .vm-icon-btn:hover { background:${T.card2} !important; }
    .vm-upload-cta:hover { border-color:${T.green} !important; background:${T.greenTint} !important; }
    .vm-filter-chip { transition:${T.tx}; }
    .vm-publish-pill:hover { background:${T.greenDark} !important; }
  `;
  document.head.appendChild(s);
}

/* Raised the cap since edited/re-encoded clips at higher bitrate
   run larger than the old unedited uploads. */
const MAX_VIDEO_MB = 250;
const TARGET_BITRATE = 8_000_000; // ~8 Mbps — noticeably crisper than default MediaRecorder output
const MAX_DIMENSION = 1080; // cap long edge; avoids absurd file sizes without visibly softening footage

const FILTERS = [
  { key: "none",  label: "Original", css: "none" },
  { key: "vivid", label: "Vivid",    css: "saturate(1.35) contrast(1.08)" },
  { key: "warm",  label: "Warm",     css: "sepia(0.18) saturate(1.15) brightness(1.03)" },
  { key: "cool",  label: "Cool",     css: "saturate(1.1) hue-rotate(-8deg) brightness(1.02)" },
  { key: "mono",  label: "Mono",     css: "grayscale(1) contrast(1.1)" },
];

function fmtTime(s) {
  if (!Number.isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
}

const canEditInBrowser = () =>
  typeof window !== "undefined" &&
  typeof HTMLCanvasElement !== "undefined" &&
  !!HTMLCanvasElement.prototype.captureStream &&
  typeof MediaRecorder !== "undefined";

/* ── Supabase helpers — unchanged from the previous version ── */
async function fetchProVideos(proId) {
  const { data, error } = await supabase.from("pro_videos").select("*").eq("pro_id", proId).order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

async function uploadMediaBlob(proId, blob, ext, folder = "") {
  const path = `${proId}/${folder}${folder ? "/" : ""}${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
  const { error } = await supabase.storage.from("pro-videos").upload(path, blob, { contentType: blob.type || undefined });
  if (error) throw error;
  const { data: pub } = supabase.storage.from("pro-videos").getPublicUrl(path);
  return pub.publicUrl;
}

/* thumbnail_url is optional — if the pro_videos table doesn't have that
   column yet, we retry without it rather than losing the whole upload. */
async function createVideoRow(proId, fields) {
  const insertRow = { pro_id: proId, ...fields, status: "published" };
  const { data, error } = await supabase.from("pro_videos").insert(insertRow).select().single();
  if (error) {
    if (fields.thumbnail_url && /thumbnail_url/i.test(error.message || "")) {
      const { thumbnail_url, ...rest } = fields;
      const { data: d2, error: e2 } = await supabase.from("pro_videos").insert({ pro_id: proId, ...rest, status: "published" }).select().single();
      if (e2) throw e2;
      return d2;
    }
    throw error;
  }
  return data;
}

async function updateVideoRow(id, fields) {
  const { error } = await supabase.from("pro_videos").update(fields).eq("id", id);
  if (error) throw error;
}

async function deleteVideoRow(id, video_url) {
  try {
    const path = video_url.split("/pro-videos/")[1];
    if (path) await supabase.storage.from("pro-videos").remove([path]);
  } catch { /* non-fatal */ }
  const { error } = await supabase.from("pro_videos").delete().eq("id", id);
  if (error) throw error;
}

const CATEGORIES = [
  "House Cleaning", "Deep Cleaning", "Plumbing", "Electrical", "TV & Electronics",
  "Hair & Beauty", "Tutoring", "Photography", "Gardening", "Carpentry",
  "Moving", "Childcare", "Fitness", "Auto Repair", "Errands", "Personal Chef",
];

function Badge({ children, variant = "green" }) {
  const map = {
    green: { bg: T.greenTint, bd: T.greenBd, col: T.greenDark },
    amber: { bg: T.amberTint, bd: T.amberBd, col: T.amber },
  };
  const s = map[variant] || map.green;
  return (
    <span style={{
      fontSize: 10.5, fontWeight: 700, padding: "3px 10px", borderRadius: T.rFull,
      background: s.bg, color: s.col, border: `1px solid ${s.bd}`, fontFamily: T.font, whiteSpace: "nowrap",
    }}>
      {children}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════
   VIDEO EDITOR — trim, filter preset, cover-frame picker.
   Logic is unchanged; only the paint layer is redesigned.
═══════════════════════════════════════════════════════════ */
function VideoEditorModal({ file, onCancel, onDone }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [srcUrl] = useState(() => URL.createObjectURL(file));
  const [duration, setDuration] = useState(0);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [filterKey, setFilterKey] = useState("none");
  const [coverTime, setCoverTime] = useState(0);
  const [coverPreview, setCoverPreview] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const supported = canEditInBrowser();

  useEffect(() => () => URL.revokeObjectURL(srcUrl), [srcUrl]);

  const handleMeta = () => {
    const v = videoRef.current;
    if (!v) return;
    setDuration(v.duration);
    setTrimEnd(v.duration);
    setCoverTime(0);
  };

  const captureFrameAsThumbnail = useCallback((time) => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const c = document.createElement("canvas");
    const scale = Math.min(1, MAX_DIMENSION / Math.max(v.videoWidth, v.videoHeight));
    c.width = Math.round(v.videoWidth * scale);
    c.height = Math.round(v.videoHeight * scale);
    const ctx = c.getContext("2d");
    ctx.filter = FILTERS.find(f => f.key === filterKey)?.css || "none";
    ctx.drawImage(v, 0, 0, c.width, c.height);
    c.toBlob(blob => { if (blob) setCoverPreview({ blob, url: URL.createObjectURL(blob) }); }, "image/jpeg", 0.9);
  }, [filterKey]);

  const setCoverToCurrent = () => {
    const v = videoRef.current;
    if (!v) return;
    setCoverTime(v.currentTime);
    captureFrameAsThumbnail(v.currentTime);
  };

  const togglePreview = () => {
    const v = videoRef.current;
    if (!v) return;
    if (playing) { v.pause(); setPlaying(false); return; }
    v.currentTime = trimStart;
    v.play();
    setPlaying(true);
  };

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => { if (v.currentTime >= trimEnd) { v.pause(); v.currentTime = trimStart; setPlaying(false); } };
    v.addEventListener("timeupdate", onTime);
    return () => v.removeEventListener("timeupdate", onTime);
  }, [trimStart, trimEnd]);

  const handleExport = async () => {
    const v = videoRef.current;
    if (!v) return;

    if (!supported) {
      onDone({ blob: file, thumbnailBlob: coverPreview?.blob || null, trimmed: false });
      return;
    }

    setProcessing(true);
    setError(null);
    try {
      const scale = Math.min(1, MAX_DIMENSION / Math.max(v.videoWidth, v.videoHeight));
      const canvas = canvasRef.current;
      canvas.width = Math.round(v.videoWidth * scale);
      canvas.height = Math.round(v.videoHeight * scale);
      const ctx = canvas.getContext("2d");
      ctx.filter = FILTERS.find(f => f.key === filterKey)?.css || "none";

      const canvasStream = canvas.captureStream(30);
      let audioTrack = null;
      try {
        const audioStream = v.captureStream ? v.captureStream() : null;
        audioTrack = audioStream?.getAudioTracks?.()[0] || null;
      } catch { /* some browsers restrict captureStream on cross-origin/local blobs — video-only fallback */ }
      const combined = new MediaStream([...canvasStream.getVideoTracks(), ...(audioTrack ? [audioTrack] : [])]);

      const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
        ? "video/webm;codecs=vp9,opus"
        : "video/webm";
      const recorder = new MediaRecorder(combined, { mimeType, videoBitsPerSecond: TARGET_BITRATE });
      const chunks = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };

      const exportDone = new Promise((resolve) => {
        recorder.onstop = () => resolve(new Blob(chunks, { type: mimeType }));
      });

      v.currentTime = trimStart;
      await new Promise(res => { v.onseeked = res; });
      recorder.start();
      v.play();

      let raf;
      const draw = () => {
        if (v.currentTime >= trimEnd || v.paused) {
          if (v.currentTime >= trimEnd) { v.pause(); recorder.stop(); cancelAnimationFrame(raf); return; }
        }
        ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
        setProgress(Math.min(100, Math.round(((v.currentTime - trimStart) / (trimEnd - trimStart)) * 100)));
        raf = requestAnimationFrame(draw);
      };
      draw();

      const blob = await exportDone;
      let thumbBlob = coverPreview?.blob || null;
      if (!thumbBlob) {
        v.currentTime = trimStart;
        await new Promise(res => { v.onseeked = res; });
        ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
        thumbBlob = await new Promise(res => canvas.toBlob(b => res(b), "image/jpeg", 0.9));
      }
      onDone({ blob, thumbnailBlob: thumbBlob, trimmed: trimStart > 0.05 || trimEnd < duration - 0.05 });
    } catch (e) {
      setError(e.message || "Couldn't process this video in-browser.");
    } finally {
      setProcessing(false);
    }
  };

  const activeFilterCss = FILTERS.find(f => f.key === filterKey)?.css || "none";

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 700, background: "rgba(13,46,26,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, fontFamily: T.font }}>
      <div style={{ background: "#fff", borderRadius: T.r16, width: "100%", maxWidth: 460, maxHeight: "92vh", overflowY: "auto", boxShadow: T.s3 }}>
        <div style={{ padding: "17px 18px", borderBottom: `1px solid ${T.borderFaint}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 15.5, fontWeight: 400, fontStyle: "italic", color: T.text, fontFamily: T.serif }}>Edit your video</span>
          <button onClick={onCancel} className="vm-icon-btn" style={{ background: "none", border: "none", cursor: "pointer", padding: 6, borderRadius: 8, display: "flex" }}><X size={16} color={T.sub} /></button>
        </div>

        <div style={{ padding: 18 }}>
          {!supported && (
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start", background: T.amberTint, border: `1px solid ${T.amberBd}`, borderRadius: T.r12, padding: "10px 12px", marginBottom: 14, fontSize: 12, color: T.amber, fontFamily: T.font, lineHeight: 1.5 }}>
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              Your browser can't trim or filter video in-app. You can still upload the original clip as-is — try Chrome or Firefox for editing.
            </div>
          )}

          <div style={{ position: "relative", width: "100%", aspectRatio: "9 / 16", maxHeight: 400, margin: "0 auto 14px", background: "#000", borderRadius: T.r14, overflow: "hidden" }}>
            <video
              ref={videoRef}
              src={srcUrl}
              onLoadedMetadata={handleMeta}
              onPause={() => setPlaying(false)}
              muted={false}
              playsInline
              style={{ width: "100%", height: "100%", objectFit: "contain", filter: activeFilterCss }}
            />
            <button onClick={togglePreview} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {!playing && (
                <div style={{ width: 48, height: 48, borderRadius: "50%", background: "rgba(255,255,255,0.88)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: T.s2 }}>
                  <Play size={20} color={T.text} fill={T.text} />
                </div>
              )}
              {playing && (
                <div style={{ position: "absolute", top: 10, right: 10, width: 30, height: 30, borderRadius: "50%", background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Pause size={14} color="#fff" fill="#fff" />
                </div>
              )}
            </button>
          </div>

          {/* TRIM */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ fontSize: 11.5, fontWeight: 700, color: T.text, fontFamily: T.font, display: "flex", alignItems: "center", gap: 5, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                <Scissors size={12} /> Trim
              </span>
              <Badge>{fmtTime(trimStart)} – {fmtTime(trimEnd)} ({fmtTime(trimEnd - trimStart)})</Badge>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div>
                <label style={{ fontSize: 10.5, color: T.faint, fontFamily: T.font, fontWeight: 600 }}>Start</label>
                <input type="range" min={0} max={duration || 0} step={0.1} value={trimStart}
                  onChange={e => { const val = Math.min(Number(e.target.value), trimEnd - 0.2); setTrimStart(Math.max(0, val)); if (videoRef.current) videoRef.current.currentTime = val; }}
                  style={{ width: "100%", accentColor: T.green }} />
              </div>
              <div>
                <label style={{ fontSize: 10.5, color: T.faint, fontFamily: T.font, fontWeight: 600 }}>End</label>
                <input type="range" min={0} max={duration || 0} step={0.1} value={trimEnd}
                  onChange={e => { const val = Math.max(Number(e.target.value), trimStart + 0.2); setTrimEnd(Math.min(duration, val)); }}
                  style={{ width: "100%", accentColor: T.green }} />
              </div>
            </div>
          </div>

          {/* FILTERS */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, color: T.text, fontFamily: T.font, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>Filter</div>
            <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 2 }}>
              {FILTERS.map(f => (
                <button key={f.key} className="vm-filter-chip" onClick={() => setFilterKey(f.key)} style={{
                  flexShrink: 0, padding: "7px 14px", borderRadius: T.rFull,
                  border: `1.5px solid ${filterKey === f.key ? T.green : T.border}`,
                  background: filterKey === f.key ? T.greenTint : "#fff",
                  color: filterKey === f.key ? T.greenDark : T.sub,
                  fontSize: 12, fontWeight: filterKey === f.key ? 700 : 500, cursor: "pointer", fontFamily: T.font,
                }}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* COVER FRAME */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ fontSize: 11.5, fontWeight: 700, color: T.text, fontFamily: T.font, textTransform: "uppercase", letterSpacing: "0.06em" }}>Cover photo</span>
              <button onClick={setCoverToCurrent} style={{ fontSize: 11.5, fontWeight: 700, color: T.green, background: "none", border: "none", cursor: "pointer", fontFamily: T.font }}>Use current frame</button>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 46, height: 62, borderRadius: 8, overflow: "hidden", border: `1.5px solid ${T.border}`, background: T.borderFaint, flexShrink: 0 }}>
                {coverPreview ? <img src={coverPreview.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : (
                  <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}><Video size={16} color={T.faint} /></div>
                )}
              </div>
              <span style={{ fontSize: 11.5, color: T.faint, fontFamily: T.font, lineHeight: 1.5 }}>
                {coverPreview ? "This frame will be the thumbnail on ProFeed." : "Pause on a frame you like, then tap \"Use current frame\"."}
              </span>
            </div>
          </div>

          {error && <div style={{ fontSize: 12, color: T.red, fontFamily: T.font, marginBottom: 10 }}>{error}</div>}

          {processing ? (
            <div>
              <div style={{ height: 6, borderRadius: 99, background: T.borderFaint, overflow: "hidden", marginBottom: 8 }}>
                <div style={{ height: "100%", width: `${progress}%`, background: T.green, transition: "width 0.15s linear" }} />
              </div>
              <div style={{ fontSize: 12, color: T.sub, fontFamily: T.font, display: "flex", alignItems: "center", gap: 6 }}>
                <Loader2 size={13} style={{ animation: "vmSpin 0.8s linear infinite" }} /> Rendering your edit… {progress}%
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={handleExport} className="vm-publish-pill" style={{ flex: 1, padding: "13px 0", background: T.green, color: "#fff", border: "none", borderRadius: T.rFull, fontSize: 13.5, fontWeight: 700, cursor: "pointer", fontFamily: T.font, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, boxShadow: T.sGreen, transition: T.tx }}>
                <Check size={15} /> Use this edit
              </button>
              <button onClick={onCancel} style={{ padding: "13px 20px", background: "#fff", border: `1.5px solid ${T.border}`, borderRadius: T.rFull, fontSize: 13.5, fontWeight: 600, cursor: "pointer", fontFamily: T.font }}>Cancel</button>
            </div>
          )}
        </div>
        <canvas ref={canvasRef} style={{ display: "none" }} />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   UPLOAD FORM
═══════════════════════════════════════════════════════════ */
function VideoUploadForm({ proId, defaultCategory, onCreated, onCancel, onToast }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState(defaultCategory || CATEGORIES[0]);
  const [rawFile, setRawFile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [edited, setEdited] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const handleFilePick = (file) => {
    if (!file) return;
    if (file.size > MAX_VIDEO_MB * 1024 * 1024) { setError(`Video must be under ${MAX_VIDEO_MB}MB.`); return; }
    setError(null);
    setRawFile(file);
    setEdited(null);
    setEditing(true);
  };

  const handleEditorDone = (result) => { setEdited(result); setEditing(false); };

  const handlePublish = async () => {
    if (!edited) { setError("Add and edit a video first."); return; }
    setUploading(true);
    setError(null);
    try {
      const videoExt = edited.blob.type?.includes("webm") ? "webm" : (rawFile.name.split(".").pop() || "mp4");
      const videoUrl = await uploadMediaBlob(proId, edited.blob, videoExt);
      let thumbnailUrl = null;
      if (edited.thumbnailBlob) {
        thumbnailUrl = await uploadMediaBlob(proId, edited.thumbnailBlob, "jpg", "covers");
      }
      const row = await createVideoRow(proId, { video_url: videoUrl, thumbnail_url: thumbnailUrl, title, description, category });
      onCreated(row);
      onToast?.("Video published to your ProFeed.");
    } catch (e) {
      setError(e.message);
      onToast?.("Couldn't publish that video — please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ padding: 20, background: T.card2, border: `1.5px solid ${T.borderFaint}`, borderRadius: T.r14, marginBottom: 18 }}>
      {editing && rawFile && (
        <VideoEditorModal file={rawFile} onCancel={() => { setEditing(false); setRawFile(null); }} onDone={handleEditorDone} />
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
        {!edited ? (
          <button onClick={() => fileRef.current.click()} className="vm-upload-cta" style={{
            display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "30px 16px",
            border: `1.5px dashed ${T.greenBd}`, borderRadius: T.r12, background: "#fff", cursor: "pointer", transition: T.tx,
          }}>
            <div style={{ width: 40, height: 40, borderRadius: "50%", background: T.greenTint, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Upload size={18} color={T.green} />
            </div>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: T.text, fontFamily: T.font }}>Choose a video to edit & post</span>
            <span style={{ fontSize: 11.5, color: T.faint, fontFamily: T.font }}>Trim, add a filter, and pick a cover before you publish</span>
          </button>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 12, background: "#fff", border: `1.5px solid ${T.border}`, borderRadius: T.r12, padding: 12 }}>
            {edited.thumbnailBlob ? (
              <img src={URL.createObjectURL(edited.thumbnailBlob)} alt="" style={{ width: 46, height: 62, borderRadius: 8, objectFit: "cover" }} />
            ) : <div style={{ width: 46, height: 62, borderRadius: 8, background: T.borderFaint }} />}
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <Check size={13} color={T.green} strokeWidth={2.4} />
                <span style={{ fontSize: 12.5, fontWeight: 700, color: T.text, fontFamily: T.font }}>{edited.trimmed ? "Edited & trimmed" : "Ready to publish"}</span>
              </div>
              <button onClick={() => setEditing(true)} style={{ fontSize: 11.5, color: T.green, fontWeight: 700, background: "none", border: "none", cursor: "pointer", fontFamily: T.font, padding: 0, marginTop: 3 }}>Edit again</button>
            </div>
          </div>
        )}
        <input ref={fileRef} type="file" accept="video/*" onChange={e => handleFilePick(e.target.files?.[0] || null)} style={{ display: "none" }} />

        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Title (e.g. Kitchen deep clean transformation)"
          style={{ padding: "11px 13px", borderRadius: T.r10, border: `1.5px solid ${T.border}`, fontSize: 13, fontFamily: T.font, outline: "none" }} />
        <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Short description"
          rows={2} style={{ padding: "11px 13px", borderRadius: T.r10, border: `1.5px solid ${T.border}`, fontSize: 13, fontFamily: T.font, resize: "vertical", outline: "none" }} />
        <select value={category} onChange={e => setCategory(e.target.value)}
          style={{ padding: "11px 13px", borderRadius: T.r10, border: `1.5px solid ${T.border}`, fontSize: 13, fontFamily: T.font, outline: "none" }}>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        {error && <div style={{ fontSize: 12.5, color: T.red, fontFamily: T.font }}>{error}</div>}

        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={handlePublish} disabled={uploading || !edited} className="vm-publish-pill" style={{
            flex: 1, padding: "12px 0", background: T.green, color: "#fff", border: "none",
            borderRadius: T.rFull, fontSize: 13, fontWeight: 700, cursor: uploading ? "default" : "pointer", fontFamily: T.font,
            opacity: !edited ? 0.55 : 1, boxShadow: !edited ? "none" : T.sGreen, transition: T.tx,
          }}>
            {uploading ? "Uploading…" : "Publish video"}
          </button>
          <button onClick={onCancel} style={{
            padding: "12px 18px", background: "#fff", border: `1.5px solid ${T.border}`,
            borderRadius: T.rFull, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: T.font,
          }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN — video list + management
═══════════════════════════════════════════════════════════ */
const SEED_VIDEOS = [
  { id: "sv1", title: "Deep clean walkthrough", category: "Deep Cleaning", video_url: "", thumbnail_url: null, status: "published", views_count: 214, likes_count: 18 },
];

export default function VideoManager({ proId, defaultCategory, isDemo, onToast }) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => {
    if (isDemo) { setVideos(SEED_VIDEOS); setLoading(false); return; }
    if (!proId) return;
    setLoading(true);
    fetchProVideos(proId).then(d => { setVideos(d); setError(null); }).catch(e => setError(e.message)).finally(() => setLoading(false));
  }, [proId, isDemo]);

  useEffect(() => { load(); }, [load]);

  const toggleStatus = async (v) => {
    const next = v.status === "published" ? "draft" : "published";
    setVideos(prev => prev.map(x => x.id === v.id ? { ...x, status: next } : x));
    if (isDemo) return;
    try { await updateVideoRow(v.id, { status: next }); } catch { load(); }
  };

  const handleDelete = async (v) => {
    setVideos(prev => prev.filter(x => x.id !== v.id));
    if (isDemo) return;
    try { await deleteVideoRow(v.id, v.video_url); } catch { load(); }
  };

  const liveCount = videos.filter(v => v.status === "published").length;

  return (
    <div style={{ background: "#fff", borderRadius: T.r16, border: `1.5px solid ${T.border}`, boxShadow: T.s1, overflow: "hidden", fontFamily: T.font }}>
      <div style={{ padding: "20px 22px", borderBottom: `1px solid ${T.borderFaint}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ width: 36, height: 36, borderRadius: "50%", background: T.greenTint, border: `1px solid ${T.greenBd}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Film size={16} color={T.green} strokeWidth={1.8} />
          </span>
          <div>
            <p style={{ margin: "0 0 3px", fontSize: 10, fontWeight: 700, color: T.green, textTransform: "uppercase", letterSpacing: "0.09em", fontFamily: T.font }}>Discover feed for clients</p>
            <div style={{ fontSize: 19, fontWeight: 400, fontStyle: "italic", color: T.text, fontFamily: T.serif, letterSpacing: "-0.01em" }}>ProFeed videos</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
              <span style={{ fontSize: 12, color: T.sub, fontFamily: T.font, fontWeight: 500 }}>{videos.length} posted</span>
              {liveCount > 0 && <Badge>{liveCount} live</Badge>}
            </div>
          </div>
        </div>
        {!showForm && (
          <button onClick={() => setShowForm(true)} className="vm-publish-pill" style={{
            display: "flex", alignItems: "center", gap: 6, padding: "11px 20px", background: T.green,
            color: "#fff", borderRadius: T.rFull, fontSize: 13, fontWeight: 700, border: "none", cursor: "pointer", fontFamily: T.font,
            boxShadow: T.sGreen, transition: T.tx,
          }}>
            <Upload size={14} /> Post a video
          </button>
        )}
      </div>

      <div style={{ padding: 22 }}>
        {showForm && (
          <VideoUploadForm
            proId={proId}
            defaultCategory={defaultCategory}
            onCreated={(row) => { setVideos(prev => [row, ...prev]); setShowForm(false); }}
            onCancel={() => setShowForm(false)}
            onToast={onToast}
          />
        )}

        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[0,1].map(i => <div key={i} style={{ height: 72, borderRadius: T.r10, background: T.card2, animation: "vmPulse 1.4s ease-in-out infinite" }} />)}
          </div>
        ) : error ? (
          <div style={{ fontSize: 13, color: T.red, fontFamily: T.font }}>{error}</div>
        ) : videos.length === 0 && !showForm ? (
          <div style={{ textAlign: "center", padding: "38px 10px", border: `1.5px dashed ${T.border}`, borderRadius: T.r14 }}>
            <div style={{ width: 46, height: 46, borderRadius: "50%", background: T.greenTint, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
              <Film size={20} color={T.green} strokeWidth={1.6} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: T.font }}>No videos yet</div>
            <div style={{ fontSize: 12.5, color: T.sub, fontFamily: T.font, marginTop: 5, maxWidth: 280, margin: "5px auto 0", lineHeight: 1.6 }}>
              Post a short video of your work. It shows up on ProFeed for clients to discover.
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {videos.map(v => (
              <div key={v.id} className="vm-row" style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", border: `1.5px solid ${T.border}`, borderRadius: T.r10 }}>
                {v.thumbnail_url ? (
                  <img src={v.thumbnail_url} alt="" style={{ width: 46, height: 62, borderRadius: 8, objectFit: "cover", flexShrink: 0, background: "#000" }} />
                ) : (
                  <video src={v.video_url} style={{ width: 46, height: 62, borderRadius: 8, objectFit: "cover", flexShrink: 0, background: "#000" }} muted />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: T.font, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{v.title || "Untitled"}</div>
                  <div style={{ fontSize: 11.5, color: T.faint, fontFamily: T.font, marginTop: 2 }}>{v.category} · {v.views_count || 0} views · {v.likes_count || 0} likes</div>
                </div>
                <Badge variant={v.status === "published" ? "green" : "amber"}>{v.status === "published" ? "Live" : "Draft"}</Badge>
                <button onClick={() => toggleStatus(v)} title={v.status === "published" ? "Unpublish" : "Publish"} className="vm-icon-btn" style={{ background: "none", border: "none", cursor: "pointer", padding: 6, borderRadius: 8, display: "flex" }}>
                  {v.status === "published" ? <EyeOff size={16} color={T.sub} /> : <Eye size={16} color={T.sub} />}
                </button>
                <button onClick={() => handleDelete(v)} title="Delete" className="vm-icon-btn" style={{ background: "none", border: "none", cursor: "pointer", padding: 6, borderRadius: 8, display: "flex" }}>
                  <Trash2 size={16} color={T.red} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}